import { HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';

/**
 * Sends the legacy Laravel session cookie back to the legacy API.
 *
 * Background: the legacy API (legacy-stokkup) runs StartSession on every
 * request with the file session driver. While the portals answered on the same
 * origin as that API the browser returned its `laravel_session` cookie and one
 * session file was reused per user. Once the apps moved to their own origins
 * the cookie stopped travelling, so Laravel minted a *new* session file on
 * every single request. That directory reached ~1.1M files / 4.4GB and the
 * in-request GC sweep over it is what produced the 5-30s API spikes.
 * `withCredentials` restores cookie reuse so the directory stops growing.
 *
 * Deliberately a separate interceptor rather than a change inside
 * AuthInterceptor, for two reasons:
 *
 *  1. AuthInterceptor short-circuits on `publicUrls`, several of which are
 *     legacy endpoints (`${apiRoot}shipments`, `${apiUrl}art/approve/`,
 *     `${apiRoot}client-portal/art/download-art-approval`, ...). Those requests
 *     create session files too, so they need the cookie as well. A separate
 *     interceptor still sees them.
 *  2. It keeps the blast radius obvious and the change trivial to revert.
 *
 * Scoped to the legacy API on purpose. Do NOT widen this to `apiUrl2`,
 * `apiMicroservice`, `apiAWS` or the CDN hosts: those backends answer with
 * `Access-Control-Allow-Origin: *`, and the browser hard-rejects a wildcard
 * Allow-Origin on a credentialed request. Adding a host here without first
 * giving it an explicit origin allow list breaks every call to it.
 *
 * Requires this app's origin to be listed in legacy-stokkup's
 * config/cors.php 'allowed_origins'.
 */
@Injectable({ providedIn: 'root' })
export class LegacyCredentialsInterceptor implements HttpInterceptor {

    /**
     * In production `apiUrl` and `uploadsUrl` both sit under `apiRoot`, but in
     * dev `apiRoot` is the relative `api/public/` served through the proxy, so
     * both are matched explicitly. `uploadsUrl` is left out: it is consumed as
     * an asset URL rather than through HttpClient, and credentials cannot be
     * attached to an <img> load anyway.
     */
    private readonly legacyApiUrls = [
        environment.apiRoot,
        environment.apiUrl,
    ].filter((url): url is string => !!url);

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
        if (request.withCredentials || !this.isLegacyApiRequest(request.url)) {
            return next.handle(request);
        }

        return next.handle(request.clone({ withCredentials: true }));
    }

    private isLegacyApiRequest(url: string): boolean {
        return this.legacyApiUrls.some(legacyUrl => url.includes(legacyUrl));
    }
}
